import React, { useEffect, useRef, useState } from 'react';
import { FileViewerProps } from './FileViewer.types';
import { FileLoader } from './FileLoader';
import { useZoomSwitch } from './FileViewer.helpers';
import { ViewerToolbar } from './ViewerToolbar/ViewerToolbar';
import { copyEventListener, useTextSelectionDispatch } from './FileViewer.hook';
import { useDocumentDownload } from './useDocumentDownload';
import { useTransformDocumentDataToDiplayableUrl } from './useTransformDocumentDataToDiplayableUrl';
import { useHighlight } from './HtmlViewer.highlight';
import { prettifyHtml } from './HtmlViewer.prettify';
import { useClauseHighlight } from './HtmlViewer.clause-highlight';
import { ScrollSyncPane } from 'react-scroll-sync';
import { DOCUMENT_ZOOM_SCALES } from '../../../config/config';
import { updateScale } from './HtmlViewer.helpers';

export const HtmlViewer = ({
  loading,
  error,
  url,
  keyword,
  method,
  order,
  searchOption,
  debug,
  clauses,
  zoomScales = DOCUMENT_ZOOM_SCALES,
  similar,
  chatMode,
}: FileViewerProps) => {
  const ref = useRef<HTMLIFrameElement>(null);
  const { scale, handleZoom } = useZoomSwitch(url, zoomScales);
  const { handleHighlight, handleSwitch } = useHighlight(ref, keyword, method);

  const { documentData, isDownloading, isError } = useDocumentDownload(loading, error, url, order);
  const { documentUrl, isLoading } = useTransformDocumentDataToDiplayableUrl(
    documentData,
    loading,
    error,
    order
  );

  const [contentRendered, setContentRendered] = useState(false);
  useClauseHighlight(ref, contentRendered, clauses, scale, similar, chatMode);

  useEffect(() => {
    setContentRendered(false);
  }, [documentUrl]);

  const { handleTextSelection } = useTextSelectionDispatch(ref);

  const iframeWindow = ref.current?.contentWindow;
  const iframeDocument = ref.current?.contentDocument;
  useEffect(() => {
    if (!iframeWindow || !iframeDocument) return;
    const copyFunction = copyEventListener(iframeWindow);
    iframeDocument.addEventListener('copy', copyFunction);

    return () => {
      iframeDocument.removeEventListener('copy', copyFunction);
    };
  }, [iframeDocument, iframeWindow]);

  const handleIframeLoad = () => {
    const iframeDoc = ref.current?.contentDocument || ref.current?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.onmouseup = handleTextSelection;
    }
    prettifyHtml(ref.current?.contentDocument?.body, debug);
    updateScale(ref, scale);
    setContentRendered(true);
    handleHighlight();
  };

  useEffect(() => {
    updateScale(ref, scale);
  }, [scale]);

  useEffect(() => {
    const currentIframe = ref.current;

    return () => {
      if (currentIframe && currentIframe.contentDocument) {
        currentIframe.contentDocument.onmouseup = null;
      }
    };
  }, []);

  return (
    <div className='file-viewer file-viewer--html'>
      {isDownloading || isLoading || isError ? (
        <FileLoader loading={isDownloading || isLoading} error={isError} />
      ) : (
        <>
          <ScrollSyncPane attachTo={{ current: ref.current?.contentDocument?.body }}>
            <iframe title='Document view' src={documentUrl} ref={ref} onLoad={handleIframeLoad} />
          </ScrollSyncPane>

          <ViewerToolbar
            handleZoom={handleZoom}
            handleSwitch={handleSwitch}
            searchOption={searchOption}
            method={method}
          />
        </>
      )}
    </div>
  );
};
